/**
 * O termo dito é o nome de um PAÍS? `"Estados Unidos"`, `"United States"`, `"EUA"` →
 * `"US"`. Existe para o resolvedor de termo do agente de voo (#6167): um país fora
 * da curadoria `AIRPORT_ALIASES` caía na busca do places, e o ranker aceitava um
 * aeroporto que só casava o texto — "EUA" virou o IATA EUA (ilha 'Eua, Tonga) e a
 * busca saiu RIO → EUA, sem inventário.
 *
 * Sem curadoria nova: os nomes vêm de `Intl.DisplayNames` (pt-BR, en, es; formas
 * longa e curta — a curta dá "US" e "UK") sobre os códigos de `country_alpha3.ts`.
 * Só as siglas que o Intl não conhece entram à mão. Chave dobrada por `foldAlias`.
 *
 * Pure (no IO): conjunto montado uma vez no load do módulo.
 */

import { countryAlpha2Codes } from './country_alpha3.js';
import { foldAlias } from './fold_alias.js';

const LOCALES = ['pt-BR', 'en', 'es'] as const;
const STYLES = ['long', 'short'] as const;

/** Siglas faladas que o Intl não devolve como nome. */
const ABBREVIATIONS: Record<string, string> = {
  eua: 'US',
  usa: 'US',
  eau: 'AE',
};

const COUNTRY_BY_NAME: ReadonlyMap<string, string> = (() => {
  const byName = new Map<string, string>(Object.entries(ABBREVIATIONS));
  for (const locale of LOCALES) {
    for (const style of STYLES) {
      const names = new Intl.DisplayNames([locale], { type: 'region', style, fallback: 'none' });
      for (const code of countryAlpha2Codes()) {
        const name = names.of(code);
        if (name && !byName.has(foldAlias(name))) byName.set(foldAlias(name), code);
      }
    }
  }
  return byName;
})();

/** O ISO alpha-2 do país que o termo nomeia, ou `null` (cidade, aeroporto, vazio). */
export function countryOfTerm(term: string | null | undefined): string | null {
  if (!term) return null;
  return COUNTRY_BY_NAME.get(foldAlias(term)) ?? null;
}
